"use client";

import { Ban, Search, ShieldCheck, UserCog, UsersRound, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AdminPagination } from "@/components/admin/admin-pagination";

type AdminUser = {
  id: string;
  name: string | null;
  email: string | null;
  image: string | null;
  role: "USER" | "ADMIN";
  isBanned: boolean;
  /** The API serialises dates as ISO strings; `new Date(…)` handles both. */
  createdAt: Date | string;
  _count: { orders: number };
};

const PER_PAGE = 10;

export function UserManager({ initialUsers }: { initialUsers: AdminUser[] }) {
  const [users, setUsers] = useState(initialUsers);
  useEffect(() => setUsers(initialUsers), [initialUsers]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<"ALL" | "USER" | "ADMIN">("ALL");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      const matchesSearch =
        !q ||
        (u.name ?? "").toLowerCase().includes(q) ||
        (u.email ?? "").toLowerCase().includes(q);
      const matchesRole = roleFilter === "ALL" || u.role === roleFilter;
      return matchesSearch && matchesRole;
    });
  }, [users, search, roleFilter]);

  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  async function updateUser(userId: string, changes: { role?: "USER" | "ADMIN"; isBanned?: boolean }) {
    const previous = users;
    setUsers((items) => items.map((u) => (u.id === userId ? { ...u, ...changes } : u)));

    try {
      const response = await fetch(`/api/admin/users/${userId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(changes)
      });
      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.message ?? "Unable to update user.");
      }
      toast.success(changes.isBanned !== undefined ? (changes.isBanned ? "User banned" : "User unbanned") : "Role updated");
    } catch (error) {
      setUsers(previous);
      toast.error(error instanceof Error ? error.message : "Unable to update user.");
    }
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900">
          <UsersRound className="h-6 w-6 text-amazon-teal" />
          <p className="mt-3 text-xs font-black uppercase text-slate-500">Users</p>
          <p className="mt-1 text-3xl font-black tracking-normal">{users.length}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900">
          <ShieldCheck className="h-6 w-6 text-amazon-orange" />
          <p className="mt-3 text-xs font-black uppercase text-slate-500">Admins</p>
          <p className="mt-1 text-3xl font-black tracking-normal">
            {users.filter((u) => u.role === "ADMIN").length}
          </p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900">
          <UserCog className="h-6 w-6 text-amazon-green" />
          <p className="mt-3 text-xs font-black uppercase text-slate-500">Total orders</p>
          <p className="mt-1 text-3xl font-black tracking-normal">
            {users.reduce((sum, u) => sum + u._count.orders, 0)}
          </p>
        </div>
      </div>

      {/* Search + Filter */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by name or email…"
            className="h-10 w-full rounded-md border border-slate-200 bg-white pl-9 pr-3 text-sm outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
          />
          {search && (
            <button
              type="button"
              onClick={() => { setSearch(""); setPage(1); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <select
          value={roleFilter}
          onChange={(e) => { setRoleFilter(e.target.value as typeof roleFilter); setPage(1); }}
          className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm outline-none focus:border-amazon-orange dark:border-white/10 dark:bg-slate-900"
        >
          <option value="ALL">All roles</option>
          <option value="USER">Users</option>
          <option value="ADMIN">Admins</option>
        </select>
        <p className="flex items-center text-sm text-slate-500">
          {filtered.length} result{filtered.length !== 1 ? "s" : ""}
        </p>
      </div>

      {users.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white p-10 text-center shadow-sm dark:border-white/10 dark:bg-slate-900">
          <p className="font-bold">No users yet</p>
        </div>
      ) : (
        <>
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-left text-sm">
                <thead className="bg-slate-100 text-xs uppercase text-slate-500 dark:bg-white/10">
                  <tr>
                    <th className="px-4 py-3">User</th>
                    <th className="px-4 py-3">Role</th>
                    <th className="px-4 py-3">Orders</th>
                    <th className="px-4 py-3">Joined</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Manage</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-white/10">
                  {paginated.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                        No users found
                      </td>
                    </tr>
                  ) : (
                    paginated.map((user) => (
                      <tr
                        key={user.id}
                        className={`hover:bg-slate-50 dark:hover:bg-white/5 ${user.isBanned ? "opacity-60" : ""}`}
                      >
                        <td className="px-4 py-4">
                          <p className="font-black text-slate-950 dark:text-white">
                            {user.name ?? "Unnamed"}
                          </p>
                          <p className="text-xs text-slate-500">{user.email}</p>
                        </td>
                        <td className="px-4 py-4">
                          <Badge tone={user.role === "ADMIN" ? "success" : "default"}>
                            {user.role}
                          </Badge>
                        </td>
                        <td className="px-4 py-4">{user._count.orders}</td>
                        <td className="px-4 py-4">
                          {new Date(user.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-4">
                          {user.isBanned ? (
                            <Badge tone="deal">Banned</Badge>
                          ) : (
                            <Badge tone="success">Active</Badge>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex flex-wrap gap-2">
                            <select
                              value={user.role}
                              onChange={(e) =>
                                updateUser(user.id, { role: e.target.value as "USER" | "ADMIN" })
                              }
                              className="h-9 rounded-md border border-slate-200 bg-transparent px-2 text-sm outline-none focus:border-amazon-orange dark:border-white/10"
                            >
                              <option>USER</option>
                              <option>ADMIN</option>
                            </select>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => updateUser(user.id, { isBanned: !user.isBanned })}
                              className={user.isBanned ? "text-green-600" : "text-red-600"}
                            >
                              <Ban className="h-3.5 w-3.5" />
                              {user.isBanned ? "Unban" : "Ban"}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <AdminPagination
            currentPage={page}
            totalItems={filtered.length}
            perPage={perPage}
            onPageChange={setPage}
            onPerPageChange={(newPerPage) => {
              setPerPage(newPerPage);
              setPage(1);
            }}
            itemLabel="users"
          />
        </>
      )}
    </div>
  );
}
